import React, { useState, useEffect } from "react";
import useAxiosInstance from "../../hooks/useAxios";
import { toast } from "react-toastify";
import FormRender from "../../components/FormRender";
import { useNavigate } from "react-router-dom";
import { FaHistory } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { CREDIT_FORM_SETTINGS } from "../../helper";

const CreditManagement = () => {
  const axiosInstance = useAxiosInstance();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    org: {},
    mode: "",
    amount: "",
    type: "",
    id_ref: "",
    title: "",
    sub_info: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [orgBalance, setOrgBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const selectedOrgId = Object.keys(formData.org)[0];

  const fetchOrgBalance = () => {
    if (!selectedOrgId) return;
    setBalanceLoading(true);
    axiosInstance.post("wkl/credits/get_org_balance/", { org_id: Number(selectedOrgId) , start_date: "1900-01-01" })
      .then((res) => setOrgBalance(res.data?.balance ?? null))
      .catch((err) => {
        toast.error("Failed to fetch balance");
        setOrgBalance(null);
      })
      .finally(() => setBalanceLoading(false));
  };

  useEffect(() => {
    if (selectedOrgId) {
      fetchOrgBalance();
    } else {
      setOrgBalance(null);
    }
  }, [selectedOrgId]);

  const handleSubmit = () => {
    if (!formData.org || Object.keys(formData.org).length === 0) {
      toast.error("Please select an organisation");
      return;
    }
    if (!formData.mode) {
      toast.error("Please select a mode");
      return;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    const orgId = Object.keys(formData.org)[0];
    axiosInstance.post("wkl/credits/update_org_credits/", {
      org_id: Number(orgId),
      amount: Number(formData.amount),
      mode: formData.mode,
      type: formData.type || undefined,
      id: formData.id_ref || undefined,
      title: formData.title || undefined,
      sub_info: formData.sub_info || undefined,
    })
      .then((res) => {
        toast.success(res.data?.message || "Credit updated successfully!");
        setFormData({
          org: formData.org,
          mode: "",
          amount: "",
          type: "",
          id_ref: "",
          title: "",
          sub_info: "",
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to update credits");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="p-5">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-black">Credit Management</h1>
          <button
            onClick={() => navigate("/credit-history")}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            <FaHistory /> View Credit History
          </button>
        </div>

        <div className="bg-white border rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Update Credits</h2>
          {selectedOrgId && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-gray-600">Selected Org Balance:</span>
              <span className="text-sm font-semibold">
                {balanceLoading ? "Loading..." : orgBalance != null ? Number(orgBalance).toFixed(2) : "-"}
              </span>
              <button
                onClick={fetchOrgBalance}
                disabled={balanceLoading}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                title="Refresh"
              >
                <MdRefresh className={balanceLoading ? "animate-spin" : ""} />
              </button>
            </div>
          )}
          <FormRender
            formData={formData}
            setFormData={setFormData}
            formSettings={CREDIT_FORM_SETTINGS}
          />
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button
              onClick={() => setFormData({
                org: {},
                mode: "",
                amount: "",
                type: "",
                id_ref: "",
                title: "",
                sub_info: "",
              })}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditManagement;